import { Button } from "@grafit/design-system/components/molecules/button";
import { Input } from "@grafit/design-system/components/molecules/input";
import { Label } from "@grafit/design-system/components/molecules/label";
import { toast } from "@grafit/design-system/components/molecules/toast";
import { Trans, useTranslation } from "@grafit/i18n";
import { CheckCircle2, XCircle } from "lucide-react";
import * as React from "react";
import { authClient } from "../auth-client";
import { assertOnline } from "../utils/assert-online";
import { storeLastLoginDate } from "../utils/store-last-login";

interface OTPSignInProps {
  email: string;
  disabled: boolean;
  isOnboarding: boolean;
}

// oxlint-disable-next-line max-lines-per-function
export const OTPSignIn = React.memo<OTPSignInProps>(({ email, disabled, isOnboarding }) => {
  const { t } = useTranslation();
  const otpId = React.useId();
  const [otp, setOTP] = React.useState("");
  const [status, setStatus] = React.useState<"idle" | "pending" | "pending-otp">("idle");
  const [otpValidation, setOtpValidation] = React.useState<"idle" | "validating" | "valid" | "invalid">("idle");

  async function signInWithOtp(trimmedEmail: string, callbackURL: string): Promise<void> {
    try {
      await authClient.signIn
        .emailOtp({
          email: trimmedEmail,
          otp,
        })
        .then((v) => {
          if (v.error) {
            throw new Error(v.error.message);
          }
          storeLastLoginDate(trimmedEmail);
          return v;
        });
      window.location.replace(callbackURL);
    } catch {
      toast.error(t((r) => r["auth.sign-in.error.generic"]));
      setStatus("pending-otp");
    }
  }

  async function sendOtp(trimmedEmail: string): Promise<void> {
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: trimmedEmail,
        type: "sign-in",
      });
      if (result.error) {
        throw new Error(result.error.message);
      }
      setStatus("pending-otp");
    } catch {
      toast.error(t((r) => r["auth.sign-in.error.generic"]));
      setStatus("idle");
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !assertOnline(t)) {
      return;
    }

    setStatus("pending");
    const callbackURL = isOnboarding ? `${window.location.origin}/onboarding` : window.location.origin;

    if (status === "pending-otp") {
      await signInWithOtp(trimmedEmail, callbackURL);
      return;
    }

    await sendOtp(trimmedEmail);
  }

  async function handleOTPBlur(): Promise<void> {
    const trimmedEmail = email.trim();
    if (!otp || !trimmedEmail) {
      return;
    }

    setOtpValidation("validating");

    try {
      const { data, error } = await authClient.emailOtp.checkVerificationOtp({
        email: trimmedEmail,
        type: "sign-in",
        otp,
      });

      // USER_NOT_FOUND means new user sign-up - treat as valid since sign-in will create the user
      if (error?.code === "USER_NOT_FOUND") {
        setOtpValidation("valid");
        return;
      }

      setOtpValidation(data?.success ? "valid" : "invalid");
    } catch {
      setOtpValidation("invalid");
    }
  }

  return (
    // oxlint-disable-next-line jsx-no-new-function-as-prop
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" method="post">
      {status === "pending-otp" && (
        <div className="space-y-2">
          <Label htmlFor={otpId}>
            <Trans i18nKey="auth.sign-in.otp.label" />
          </Label>
          <Input
            id={otpId}
            name="otp"
            type="text"
            value={otp}
            // oxlint-disable-next-line jsx-no-new-function-as-prop
            onChange={(e) => {
              setOTP(e.target.value);
              setOtpValidation("idle");
            }}
            // oxlint-disable-next-line jsx-no-new-function-as-prop
            onBlur={() => void handleOTPBlur()}
            placeholder={t((r) => r["auth.sign-in.otp.placeholder"])}
            autoComplete="one-time-code"
            required
            disabled={disabled}
            endContent={
              otpValidation === "valid" ? (
                // oxlint-disable-next-line jsx-no-jsx-as-prop
                <CheckCircle2 className="text-green-600" />
              ) : otpValidation === "invalid" ? (
                <XCircle className="text-red-600" />
              ) : null
            }
          />
        </div>
      )}
      <Button
        variant={{ kind: "primary", renderMode: "fill", size: "m" }}
        type="submit"
        className="w-full"
        isPending={status === "pending"}
        disabled={
          disabled || status === "pending" || !email.trim() || (status === "pending-otp" && otpValidation === "invalid")
        }
        enableCheckmark
      >
        <Trans
          i18nKey={
            status === "pending-otp"
              ? "auth.sign-in.otp.connect"
              : status === "pending"
                ? "auth.sign-in.button.sending"
                : "auth.sign-in.otp.button.send"
          }
        />
      </Button>
    </form>
  );
});
OTPSignIn.displayName = "OTPSignIn";
